/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { type ContentStackStorePageBody } from '@/features/contentstack/interface';

export type StoreWarning = NonNullable<
  ContentStackStorePageBody['store_information']['warning_notifications']
>[number];

const CITY_MATCH = 2;
const STATE_MATCH = 1;
const GLOBAL_MATCH = 0;
const NO_MATCH = null;

/**
 * Mirrors `isLocationFilterMatching`, but ranks by specificity instead of returning a boolean.
 * Entries the server already filtered out never reach this, so `NO_MATCH` only guards the case
 * where the content was fetched without a state/city (no server-side filtering applied).
 */
const getMatchRank = (
  warning: StoreWarning,
  state: string | undefined,
  city: string | undefined,
): number | null => {
  if (!warning.location_filter_enabled) {
    return GLOBAL_MATCH;
  }

  const { states = [], cities = [] } = warning.location_filter ?? {};

  if (states.length === 0 && cities.length === 0) {
    return GLOBAL_MATCH;
  }

  if (cities.length > 0) {
    const stateMatches = states.length === 0 || (!!state && states.includes(state));
    const cityMatches = !!city && cities.includes(city);

    return stateMatches && cityMatches ? CITY_MATCH : NO_MATCH;
  }

  return !!state && states.includes(state) ? STATE_MATCH : NO_MATCH;
};

/**
 * Picks the most specific warning message for the current store: city over state, state over
 * global. Warnings without a message are skipped so a targeted-but-empty entry never shadows a
 * global one. Ties keep the first entry, so authoring order is the tiebreaker.
 */
export const pickStoreWarning = (
  warnings: StoreWarning[] | undefined,
  { state, city }: { state: string | undefined; city: string | undefined },
): string | undefined => {
  let bestWarning: StoreWarning | undefined;
  let bestRank = -1;

  for (const warning of warnings ?? []) {
    if (!warning.message?.trim()) {
      continue;
    }

    const rank = getMatchRank(warning, state, city);

    if (rank !== NO_MATCH && rank > bestRank) {
      bestWarning = warning;
      bestRank = rank;
    }
  }

  return bestWarning?.message;
};
